import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useSiteSettings } from "../../context/site-settings";
import { getAboutContent } from "../../services/site-content";
import { socialLinks } from "../../config/social";
import { profileData } from "../../config/profile";
import GameManager from "../games/game-manager";

interface TermLine {
  text: string;
  kind: "in" | "out" | "err";
}

const USER = "guest";
const HOST = "emilsd-web";

const STATIC_FILES: Record<string, string[]> = {
  "secrets.txt": [
    "you didn't think it would be that easy, did you?",
    "hint: there are three games hidden on this site. run 'games'.",
  ],
  motd: ["welcome to emilsd-web. don't feed the snake."],
};

const FILE_NAMES = ["about.md", ...Object.keys(STATIC_FILES)];

const COMMANDS = [
  "help",
  "ls",
  "cd",
  "cat",
  "open",
  "pwd",
  "whoami",
  "date",
  "echo",
  "history",
  "neofetch",
  "social",
  "contact",
  "source",
  "github",
  "games",
  "crt",
  "sudo",
  "clear",
  "exit",
];

function SiteTerminal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSiteSettings();
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<TermLine[]>([]);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [booted, setBooted] = useState(false);
  const [showGames, setShowGames] = useState(false);
  const aboutCache = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const pages: Record<string, string> = {
    home: "/",
    projects: "/projects",
    ...(settings.blog_enabled ? { blog: "/blog" } : {}),
    about: "/about",
    resume: "/resume",
    admin: "/admin",
    arcade: "/",
  };

  const prompt = `${USER}@${HOST}:${
    location.pathname === "/" ? "~" : location.pathname
  }$`;

  const print = useCallback((out: string[], kind: TermLine["kind"] = "out") => {
    setLines((prev) => [...prev, ...out.map((text) => ({ text, kind }))]);
  }, []);

  // global hotkeys: physical key below ESC opens, Esc closes; header button event
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (e.code === "Backquote" && !open && !typing) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    const onToggle = () => setOpen((v) => !v);
    window.addEventListener("keydown", onKey);
    window.addEventListener("site-terminal:toggle", onToggle);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("site-terminal:toggle", onToggle);
    };
  }, [open]);

  // restore CRT mode from a previous visit
  useEffect(() => {
    if (localStorage.getItem("crt") === "1") {
      document.documentElement.classList.add("crt");
    }
  }, []);

  useEffect(() => {
    if (open) {
      if (!booted) {
        setBooted(true);
        print([
          "emilsd-web tty1  —  type 'help' for commands, 'exit' or ESC to close",
        ]);
      }
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, booted, print]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [lines, open]);

  const neofetch = (): string[] => {
    const uptimeYears = new Date().getFullYear() - 2023;
    const info: [string, string][] = [
      ["", `${USER}@${HOST}`],
      ["", "----------------"],
      ["os", "web/1.0 (vite + react)"],
      ["host", "cloudflare pages"],
      ["kernel", "typescript"],
      ["uptime", `${uptimeYears}+ years`],
      ["shell", "bash (emulated, poorly)"],
      ["db", "supabase postgres"],
      ["editor", "whatever works"],
      ["display", `${window.innerWidth}x${window.innerHeight} [monochrome]`],
    ];
    const logo = [
      "        ▄▄▄▄▄▄        ",
      "      ▄████████▄      ",
      "     ████████████     ",
      "     ███  ██  ███     ",
      "     ████████████     ",
      "     ▀██████████▀     ",
      "      ▄▄██████▄▄      ",
      "     ████████████     ",
      "                      ",
      "                      ",
    ];
    return logo.map((l, i) => {
      const row = info[i];
      if (!row) return l;
      return `${l}${row[0] ? `${row[0].padEnd(9)}${row[1]}` : row[1]}`;
    });
  };

  const catAbout = () => {
    if (aboutCache.current !== null) {
      print(aboutCache.current.split("\n"));
      return;
    }
    print(["fetching about.md..."]);
    getAboutContent()
      .then((content) => {
        aboutCache.current = content;
        print(content.split("\n"));
      })
      .catch(() => print(["cat: about.md: failed to load"], "err"));
  };

  const run = (raw: string) => {
    const line = raw.trim();
    print([`${prompt} ${raw}`], "in");
    if (!line) return;
    setHistory((prev) => [...prev, line]);
    setHistIdx(-1);

    const [cmd, ...args] = line.split(/\s+/);
    const arg = args.join(" ");

    switch (cmd) {
      case "help":
        print([
          "ls [-a]         list pages and files",
          "cd <page>       go to a page (also: open)",
          "cat <file>      print a file",
          "neofetch        system info",
          "social          where to find me",
          "contact         open linkedin",
          "source          this site on github",
          "games           open the arcade (from anywhere)",
          "crt             toggle CRT scanlines (persists)",
          "pwd / whoami    the usual",
          "history         command history",
          "clear / exit    tidy up / close (ctrl+l works too)",
        ]);
        break;
      case "ls": {
        const dirs = Object.keys(pages)
          .filter((p) => p !== "home" && p !== "admin")
          .map((p) => `${p}/`);
        const out = [["~/", ...dirs].join("  "), FILE_NAMES.join("  ")];
        if (args.includes("-a")) out.push(".admin/  .git");
        print(out);
        break;
      }
      case "cd":
      case "open": {
        const key = arg.replace(/^[./]+|\/+$/g, "") || "home";
        if (key === "arcade") {
          setShowGames(true);
        } else if (pages[key]) {
          navigate(pages[key]);
          print([`→ ${pages[key]}`]);
        } else if (key === "blog" && !settings.blog_enabled) {
          print(["cd: blog: currently offline"], "err");
        } else {
          print([`cd: no such page: ${arg}`], "err");
        }
        break;
      }
      case "cat":
        if (!arg) {
          print(["cat: missing operand"], "err");
        } else if (arg === "about.md") {
          catAbout();
        } else if (arg === ".git") {
          print(["fatal: this is not the repo you're looking for"], "err");
        } else if (STATIC_FILES[arg]) {
          print(STATIC_FILES[arg]);
        } else {
          print([`cat: ${arg}: no such file`], "err");
        }
        break;
      case "pwd":
        print([location.pathname]);
        break;
      case "whoami":
        print([
          "visitor — but i'm emil. software engineering student,",
          "based in denmark. this site is my sandbox.",
        ]);
        break;
      case "date":
        print([new Date().toString()]);
        break;
      case "echo":
        print([arg]);
        break;
      case "history":
        print(history.map((h, i) => `  ${i + 1}  ${h}`));
        break;
      case "neofetch":
        print(neofetch());
        break;
      case "social":
        print([
          `github    ${socialLinks.github}`,
          `linkedin  ${socialLinks.linkedin}`,
          `twitter   ${socialLinks.twitter}`,
          `youtube   ${socialLinks.youtube}`,
        ]);
        break;
      case "contact":
        print(["opening linkedin..."]);
        window.open(socialLinks.linkedin, "_blank", "noopener");
        break;
      case "source":
        print(["opening github repo..."]);
        window.open(
          `${socialLinks.github}/${profileData.githubRepo}`,
          "_blank",
          "noopener"
        );
        break;
      case "github":
        print(["opening github..."]);
        window.open(socialLinks.github, "_blank", "noopener");
        break;
      case "games":
        setShowGames(true);
        break;
      case "crt": {
        const on = document.documentElement.classList.toggle("crt");
        localStorage.setItem("crt", on ? "1" : "0");
        print([on ? "CRT mode on. mind the burn-in." : "CRT mode off."]);
        break;
      }
      case "snake":
      case "tetris":
      case "tictactoe":
        print([`${cmd}: binary lives in the arcade. run 'games'.`]);
        break;
      case "sudo":
        print(
          [
            `${USER} is not in the sudoers file.`,
            "this incident will be reported.",
          ],
          "err"
        );
        break;
      case "rm":
        print(
          [args.includes("-rf") ? "nice try." : "rm: permission denied"],
          "err"
        );
        break;
      case "clear":
        setLines([]);
        break;
      case "exit":
      case "close":
        setOpen(false);
        break;
      default:
        print([`sh: command not found: ${cmd}`], "err");
    }
  };

  const complete = () => {
    const parts = input.split(/\s+/);
    const pool =
      parts.length <= 1
        ? COMMANDS
        : parts[0] === "cat"
          ? FILE_NAMES
          : ["cd", "open"].includes(parts[0])
            ? Object.keys(pages)
            : [];
    const last = parts[parts.length - 1];
    const hits = pool.filter((c) => c.startsWith(last));
    if (hits.length === 1) {
      parts[parts.length - 1] = hits[0];
      setInput(parts.join(" ") + (parts.length === 1 ? " " : ""));
    } else if (hits.length > 1) {
      print([hits.join("  ")]);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      run(input);
      setInput("");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const idx = histIdx < 0 ? history.length - 1 : Math.max(0, histIdx - 1);
      if (history[idx] !== undefined) {
        setHistIdx(idx);
        setInput(history[idx]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (histIdx >= 0 && histIdx < history.length - 1) {
        setHistIdx(histIdx + 1);
        setInput(history[histIdx + 1]);
      } else {
        setHistIdx(-1);
        setInput("");
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      complete();
    } else if (e.key === "l" && e.ctrlKey) {
      e.preventDefault();
      setLines([]);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed top-0 left-0 right-0 z-[100] h-[45vh] min-h-[260px]
                       bg-black/95 backdrop-blur-sm border-b border-white/30
                       font-mono text-sm flex flex-col"
            onClick={() => inputRef.current?.focus()}
          >
            <div className="flex items-center justify-between px-3 py-1 border-b border-white/10 text-white/40 text-xs select-none">
              <span>/dev/tty1 — emilsd-web</span>
              <button
                onClick={() => setOpen(false)}
                className="hover:text-white px-1"
                aria-label="Close terminal"
              >
                [x]
              </button>
            </div>
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5"
            >
              {lines.map((l, i) => (
                <pre
                  key={i}
                  className={`whitespace-pre-wrap break-words leading-snug ${
                    l.kind === "in"
                      ? "text-white"
                      : l.kind === "err"
                        ? "text-red-400/90"
                        : "text-white/70"
                  }`}
                >
                  {l.text || " "}
                </pre>
              ))}
              <div className="flex gap-2 items-center">
                <span className="text-green-400 shrink-0">{prompt}</span>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  className="flex-1 bg-transparent outline-none text-white caret-green-400"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoComplete="off"
                  aria-label="Terminal input"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <GameManager isOpen={showGames} onClose={() => setShowGames(false)} />
    </>
  );
}

export default SiteTerminal;
